/**
 * POSITION LAYOUT UTILITIES
 * 
 * Handles the complete tree layout pipeline:
 * - Phase 1: Subtree width calculation (bottom-up)
 * - Phase 2: Position assignment (top-down)
 * - Phase 3: Collision resolution (iterative refinement)
 * - Phase 4: Coordinate normalization
 * - Disconnected node placement
 */

import {
  NODE_WIDTH, LEVEL_HEIGHT, MIN_NODE_GAP, SUBTREE_GAP, TREE_PADDING,
  MAX_COLLISION_ITERATIONS,
} from '../../../../Config/ExistingVisaTreeViewConsts.js';

// ── Phase 1: Subtree Widths (Bottom-Up) ──────────────────────────────────

/**
 * Calculate the width needed by each subtree.
 * Bottom-up traversal to determine layout constraints.
 * 
 * @param {string} nodeId - Current node ID
 * @param {Object} children - Parent-child relationships
 * @param {Object} widths - Output map { [nodeId]: width }
 * @returns {number} Width of subtree rooted at nodeId
 */
export const calcWidth = (nodeId, children, widths) => {
  const kids = children[nodeId];
  
  if (!kids || kids.length === 0) {
    widths[nodeId] = NODE_WIDTH;
    return NODE_WIDTH;
  }

  let total = 0;
  for (let i = 0; i < kids.length; i++) {
    total += calcWidth(kids[i].node.id, children, widths);
    if (i < kids.length - 1) total += SUBTREE_GAP;
  }
  
  widths[nodeId] = Math.max(NODE_WIDTH, total);
  return widths[nodeId];
};

// ── Phase 2: Position Assignment (Top-Down) ─────────────────────────────

/**
 * Assign positions to nodes in a top-down traversal.
 * Centers each node within its allocated subtree width.
 * 
 * @param {string} nodeId - Current node ID
 * @param {number} left - Left boundary of allocated space
 * @param {number} depth - Tree depth (level)
 * @param {Object} children - Parent-child relationships
 * @param {Object} widths - Subtree widths from Phase 1
 * @param {Object} positions - Output map { [nodeId]: { x, y, level } }
 */
export const assign = (nodeId, left, depth, children, widths, positions) => {
  positions[nodeId] = {
    x: left + (widths[nodeId] - NODE_WIDTH) / 2,
    y: depth * LEVEL_HEIGHT + TREE_PADDING,
    level: depth,
  };
  
  const kids = children[nodeId];
  if (!kids) return;
  
  let childLeft = left;
  for (const c of kids) {
    assign(c.node.id, childLeft, depth + 1, children, widths, positions);
    childLeft += widths[c.node.id] + SUBTREE_GAP;
  }
};

// ── Phase 3: Collision Resolution ────────────────────────────────────────

/**
 * Shift a subtree horizontally to resolve collisions.
 * Uses iterative DFS to avoid stack overflow on deep trees.
 * 
 * @param {string} nodeId - Root of subtree to shift
 * @param {number} dx - Horizontal displacement
 * @param {Object} children - Parent-child relationships
 * @param {Object} positions - { [nodeId]: { x, y, level } }
 */
const shiftSubtree = (nodeId, dx, children, positions) => {
  const stack = [nodeId];
  const seen = new Set();
  
  while (stack.length) {
    const id = stack.pop();
    if (seen.has(id)) continue;
    seen.add(id);
    
    if (positions[id]) positions[id].x += dx;
    
    const kids = children[id];
    if (kids) {
      for (const c of kids) stack.push(c.node.id);
    }
  }
};

/**
 * Build level-based grouping of nodes for collision detection.
 * 
 * @param {Object} positions - Current node positions
 * @returns {Object} { [level]: [nodeIds] } sorted by X coordinate
 */
const buildLevels = (positions) => {
  const lvls = {};
  
  for (const id in positions) {
    const l = positions[id].level;
    (lvls[l] || (lvls[l] = [])).push(id);
  }
  
  // Sort each level by X coordinate for sequential collision checks
  for (const l in lvls) {
    lvls[l].sort((a, b) => positions[a].x - positions[b].x);
  }
  
  return lvls;
};

/**
 * Resolve horizontal collisions between nodes at the same level.
 * Uses iterative refinement with configurable iteration limit.
 * 
 * @param {Object} positions - Current node positions
 * @param {Object} nodeHeights - Node dimensions
 * @param {Object} children - Parent-child relationships
 */
export const resolveCollisions = (positions, nodeHeights, children) => {
  let levels = buildLevels(positions);
  
  for (let iter = 0; iter < MAX_COLLISION_ITERATIONS; iter++) {
    let changed = false;
    
    for (const l in levels) {
      const row = levels[l];
      
      // Check each node against the previous node in the row
      for (let i = 1; i < row.length; i++) {
        const prevNode = row[i - 1];
        const currNode = row[i];
        const gap = positions[currNode].x - (positions[prevNode].x + NODE_WIDTH);
        
        if (gap < MIN_NODE_GAP) {
          // Shift current node and its entire subtree
          shiftSubtree(currNode, MIN_NODE_GAP - gap, children, positions);
          changed = true;
        }
      }
    }
    
    if (!changed) break; // Converged
    levels = buildLevels(positions);
  }
};

// ── Phase 4: Coordinate Normalization ───────────────────────────────────

/**
 * Normalize tree to canvas origin and compute final bounds.
 * 
 * @param {Object} positions - Current node positions
 * @param {Object} nodeHeights - Node dimensions
 * @returns {Object} { width, height }
 */
export const normalizeToBounds = (positions, nodeHeights) => {
  let minX = Infinity, maxX = -Infinity, maxY = 0;
  
  for (const id in positions) {
    const p = positions[id];
    
    if (p.x < minX) minX = p.x;
    if (p.x + NODE_WIDTH > maxX) maxX = p.x + NODE_WIDTH;
    
    const bot = p.y + (nodeHeights[id] || 40); // Default height fallback
    if (bot > maxY) maxY = bot;
  }
  
  // Shift all nodes to canvas origin
  for (const id in positions) {
    positions[id].x += TREE_PADDING - minX;
  }
  
  return {
    width: maxX - minX + TREE_PADDING * 2,
    height: maxY + TREE_PADDING,
  };
};

// ── Disconnected Node Placement ─────────────────────────────────────────

/**
 * Place disconnected nodes in a staging row below the main tree.
 * Nodes are arranged in a grid pattern for organization.
 * 
 * @param {Array} disconnected - Array of unpositioned node IDs
 * @param {Object} positions - Current node positions
 * @param {Object} nodeHeights - Node dimensions
 */
export const placeDisconnectedNodes = (disconnected, positions, nodeHeights) => {
  if (disconnected.length === 0) return;
  
  // Determine grid parameters
  const perRow = Math.max(1, Math.floor(1400 / (NODE_WIDTH + MIN_NODE_GAP)));
  let startY = TREE_PADDING;
  
  // Position below existing tree if any
  if (Object.keys(positions).length > 0) {
    const maxAssignedY = Object.keys(positions).reduce((max, id) => {
      const h = nodeHeights[id] || 40;
      return Math.max(max, positions[id].y + h);
    }, TREE_PADDING);
    startY = maxAssignedY + LEVEL_HEIGHT;
  }
  
  // Grid placement
  disconnected.forEach((id, idx) => {
    const row = Math.floor(idx / perRow);
    const col = idx % perRow;
    positions[id] = {
      x: col * (NODE_WIDTH + MIN_NODE_GAP),
      y: startY + row * LEVEL_HEIGHT,
      level: 100 + row, // High level number for disconnected nodes
    };
  });
};
